import React from "react";
import { Box } from "@mui/material";

interface HighlightedTextProps {
  text: string | number | null | undefined;
  highlight: string;
}

/**
 * Component to highlight parts of a text string that match a search query
 * Ensures no extra spacing is added to the right of the highlighted text
 */
export const ShiftSchedulesHighlightedText: React.FC<HighlightedTextProps> = ({
  text,
  highlight,
}) => {
  // Handle undefined/null values
  if (text === undefined || text === null) {
    return null;
  }

  // If there's no search term or it's empty, just return the text
  if (!highlight || highlight.trim() === "") {
    return <>{text}</>;
  }

  const stringText = String(text);
  const highlightLower = highlight.toLowerCase();
  const textLower = stringText.toLowerCase();

  // If highlight term is not found in text, return original text
  if (!textLower.includes(highlightLower)) {
    return <>{text}</>;
  }

  // Split the text on the highlight term and join with highlighted spans
  const parts = stringText.split(new RegExp(`(${highlight})`, "gi"));

  return (
    <Box sx={{ display: "flex", flexWrap: "nowrap", width: "100%" }}>
      {parts.map((part, i) => {
        const isHighlighted = part.toLowerCase() === highlightLower;
        return isHighlighted ? (
          <Box
            key={i}
            component="span"
            sx={{
              backgroundColor: "rgba(255, 235, 59, 0.5)", // Yellow highlight
              borderRadius: "2px",
              fontWeight: "bold",
              userSelect: "text", // Ensure text selection works
              display: "inline",
              whiteSpace: "pre",
            }}
          >
            {part}
          </Box>
        ) : (
          <span key={i} style={{ whiteSpace: "pre" }}>
            {part}
          </span>
        );
      })}
    </Box>
  );
};
